import React, { useState } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useParams } from 'react-router';

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { Alert, Box, Snackbar } from '@mui/material';

import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import { disputeResolvedEndPoint } from '../../../../common/api.endPoints';
import { IconLink } from '../../../../common/components/IconLink';
import { TaskState } from '../../../../common/constants/states';
import { HttpService } from '../../../../common/http.service';
import { syncApplicationState } from '../../../detail/detailSlice';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

function userFacingErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) {
      return m;
    }
  }
  return fallback;
}

export const DisputeResolved = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: string }>();

  const tasks = useSelector(state => state.taskList.entities);

  // Exclude synthetic navigation tasks (negative IDs: DECISION, OVERVIEW, NOTES, etc.)
  // which always have state DEFAULT and would permanently block this condition.
  const realTasks = tasks
    ? Object.values(tasks).filter(t => (t?.id ?? 0) > 0)
    : [];
  const allTasksDoneOrWaived =
    realTasks.length > 0 &&
    realTasks.every(
      t => t?.state === TaskState.COMPLETED || t?.state === TaskState.WAIVED,
    );

  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  if (!id) {
    return null;
  }

  const handleClick = async () => {
    if (isLoading || !allTasksDoneOrWaived) return;

    setIsLoading(true);
    try {
      const response = await HttpService.getInstance().post(
        disputeResolvedEndPoint(id),
        {},
      );

      if (response && !response.error) {
        dispatch(syncApplicationState(id));
        setSnackbar({
          open: true,
          message: 'Dispute resolved. Case moved to Completed Investigation.',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: userFacingErrorMessage(
            response?.error,
            'Unable to resolve dispute. Ensure all tasks are complete.',
          ),
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to resolve dispute. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Box
        sx={{
          pointerEvents: allTasksDoneOrWaived ? 'auto' : 'none',
          filter: allTasksDoneOrWaived ? 'none' : 'grayscale(1) opacity(0.7)',
        }}
        title={
          !allTasksDoneOrWaived
            ? 'All tasks must be completed or waived before resolving the dispute'
            : undefined
        }
      >
        <IconLink
          onClick={handleClick}
          icon={faCheckCircle}
          copy={isLoading ? 'Processing...' : 'Dispute Resolved'}
        />
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '40px' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%', minWidth: '400px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DisputeResolved;
